
// Performance optimization utilities - Silent Mode

// Debounce utility for expensive operations
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Bundle size monitoring - completely silent
export const monitorBundleSize = () => {
  // Silent monitoring - no console output
  if (typeof window !== 'undefined' && 'performance' in window && process.env.NODE_ENV === 'development') {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    // Monitor silently without any console output
  }
};

// Critical rendering path optimization - silent mode
export const optimizeRenderPath = () => {
  // Analyze stylesheet usage (dev only) - silently
  const analyzeStylesheets = () => {
    if (process.env.NODE_ENV !== 'development') return;
    
    const sheets = Array.from(document.styleSheets);
    let totalRules = 0;
    sheets.forEach(sheet => {
      try {
        const rules = Array.from(sheet.cssRules || []);
        totalRules += rules.length;
      } catch (e) {
        // Cross-origin stylesheets - ignore silently
      }
    });
    
    // Silent monitoring - no console output
  };

  // Simplified CSS optimization - avoid MIME type issues
  const optimizeNonCriticalCSS = () => {
    // Only optimize explicitly marked stylesheets to avoid MIME type errors
    const deferredStyles = document.querySelectorAll('link[rel="stylesheet"][data-defer="true"]');
    deferredStyles.forEach(link => {
      const linkEl = link as HTMLLinkElement;
      linkEl.media = 'print';
      linkEl.onload = () => linkEl.media = 'all';
    });
  };

  analyzeStylesheets();
  optimizeNonCriticalCSS();
};

// Image optimization - silent mode
export const optimizeImages = () => {
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    // Add loading="lazy" for below-the-fold images
    if (!img.hasAttribute('loading')) {
      const rect = img.getBoundingClientRect();
      if (rect.top > window.innerHeight) {
        img.loading = 'lazy';
      }
    }
  });
};

// Memory management - silent mode
export const createMemoryOptimizedComponent = <T extends object>(
  component: T,
  dependencies: any[] = []
): T => {
  // Simplified memory optimization - in React use useMemo/useCallback
  return new Proxy(component, {
    get(target, prop) {
      const value = target[prop as keyof T];
      if (typeof value === 'function') {
        // Cache function calls
        return (...args: any[]) => {
          const cacheKey = `${String(prop)}_${JSON.stringify(args)}`;
          if (!(window as any).__fnCache) {
            (window as any).__fnCache = new Map();
          }
          
          if ((window as any).__fnCache.has(cacheKey)) {
            return (window as any).__fnCache.get(cacheKey);
          }
          
          const result = value.apply(target, args);
          (window as any).__fnCache.set(cacheKey, result);
          return result;
        };
      }
      return value;
    }
  });
};

// Initialize all optimizations - completely silent
export const initializePerformanceOptimizations = () => {
  // Run on DOM ready - silently
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      optimizeRenderPath();
      optimizeImages();
      monitorBundleSize();
    });
  } else {
    optimizeRenderPath();
    optimizeImages();
    monitorBundleSize();
  }
};
